import React, { useRef, useState, useLayoutEffect } from 'react';
import { Flex } from '@components/Flex';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import {
  BlogCenterTextPairing,
  MainBackgroundImage,
  StyledMobileSection,
  StyledBox,
  NoFeeCashEverWrapper,
} from './RegistryCashFreeGiftingSection.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { defaultBase64Placeholder } from '@components/CloudImage';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { useTranslation } from 'react-i18next';

interface RegistryCashFreeGiftingSectionProps {}

export const RegistryCashFreeGiftingSection: React.FC<RegistryCashFreeGiftingSectionProps> = () => {
  const CashFreeGiftingRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  let [isScrolledIntoView, setScrolledIntoView] = useState(false);
  const { t } = useTranslation('cashFundRegistry');
  const publicCatalogSection = t('noFeeCashEverWrapper');
  const getStarted = t('getStarted');

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let RegistryCash: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController) {
          RegistryCash = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          RegistryCash.fromTo(
            CashFreeGiftingRef.current,
            {
              transform: 'translateY(20px)',
            },
            {
              transform: 'translateY(-20px)',
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#RegistryCash',
          triggerHook: 'onEnter',
          duration: '100%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            RegistryCash?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (RegistryCash) {
        RegistryCash.progress(0);
        RegistryCash.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted]);

  return (
    <StyledMobileSection as="section" id="Cash_Funds" marginTop={[12, 0]}>
      <MainBackgroundImage backgroundImage={isScrolledIntoView ? 'cash-fund/zero-fee-couple.jpg' : defaultBase64Placeholder}>
        <LazyLoadWrapper beforeLoad={() => setScrolledIntoView(true)}>
          <div />
        </LazyLoadWrapper>
        <NoFeeCashEverWrapper flexDirection="column" alignItems="center">
          <StyledBox alignItems="center">
            <BlogCenterTextPairing
              alignment="center"
              title={publicCatalogSection.title}
              eyebrow={publicCatalogSection.fee2BlogText}
              eyebrowColor="humana4"
              eyebrowVariant="sectionEyeBrow20"
              eyebrowTagName="h2"
              titleVariant="hed72Hero"
              titleColor="neutra6"
              titleTagName="h2"
              body={publicCatalogSection.body}
              bodyVariant="dek24"
              bodyColor="neutra6"
              titleMaxWidth="507px"
              bodyTagName="p"
            />
          </StyledBox>
          <Flex flexDirection={'row'} alignItems={'center'} justifyItems={'center'} marginTop={[7, 7]} marginBottom={[7, 7]}>
            <GetStartedButton id="cashRegistryFreeGiftingStartedBtn" page={'registry'} variant="primary">
              {getStarted}
            </GetStartedButton>
          </Flex>
        </NoFeeCashEverWrapper>
      </MainBackgroundImage>
    </StyledMobileSection>
  );
};
